import {
  AnomalySensitivity,
  AutomatedTestConfigurationApi,
  DataContractConfigApi,
  FetchMetadataValuesApi,
  SensitivityEstimation,
  TestPollingApi,
  TestResult,
  UpdateCloudTestResponse,
  UpdateCloudTestsResponse,
  UpdateTestApi,
} from '@elementary/common-ui/types';
import { get, put, post, patch, del } from '@elementary/common-ui/utils/http';
import { gql } from 'graphql-request';
import {
  CreateOverrideAlertRuleApi,
  FetchAlertIntegrationOptionsApi,
  FlagsApi,
  FetchPullRequestsApi,
  AddTestApi,
  AddSingularTestApi,
  ConvertQueryApi,
  ConvertQueryApiResult,
  GenerateCustomSqlApi,
  GenerateCustomSqlResult,
  FetchDwhApi,
  EditGenericTestApi,
  FetchIntegrationDestinationOptionsApi,
  AddCloudTestApi,
  AddCustomSqlCloudTestApi,
  CheckCreatableTestsApi,
  UpdateCustomSqlConfigApi,
  SimulateTestResultApi,
  ValidateCustomSqlTestApi,
  ValidateCustomSqlTestResult,
} from '../types';

export const fetchExtendedMetadataValueOptions = (
  envId: string,
): Promise<FetchMetadataValuesApi> =>
  get(
    `${
      import.meta.env.VITE_API_BASE_URL
    }/${envId}/resources/extended-metadata-options`,
  );

export const fetchFlags = (): Promise<FlagsApi> =>
  get(`${import.meta.env.VITE_API_BASE_URL}/flags/`);

export const fetchPullRequests = (
  envId: string,
): Promise<FetchPullRequestsApi> =>
  get(
    `${import.meta.env.VITE_API_BASE_URL}/environments/${envId}/pull-requests`,
  );

export const updateCloudTestConfig = (
  envId: string,
  testId: string,
  config: AutomatedTestConfigurationApi,
): Promise<void> =>
  put(
    `${
      import.meta.env.VITE_API_BASE_URL
    }/${envId}/cloud-tests/tests/${testId}/config`,
    config,
  );

export const updateDataContractConfig = (
  envId: string,
  testId: string,
  config: DataContractConfigApi,
): Promise<void> =>
  put(
    `${
      import.meta.env.VITE_API_BASE_URL
    }/${envId}/cloud-tests/tests/${testId}/data-contract-config`,
    config,
  );

export const fetchCurrentSchema = (
  envId: string,
  testId: string,
): Promise<{
  columns: NonNullable<DataContractConfigApi['baseline_columns']>;
}> =>
  get(
    `${
      import.meta.env.VITE_API_BASE_URL
    }/${envId}/cloud-tests/tests/${testId}/current-schema`,
  );

export const updateTest = (
  envId: string,
  testId: string,
  data: UpdateTestApi,
): Promise<UpdateCloudTestResponse> =>
  patch(
    `${import.meta.env.VITE_API_BASE_URL}/${envId}/cloud-tests/tests/${testId}`,
    data,
  );

export const updateCloudTests = (
  envId: string,
  tests: { test_id: string; disabled: boolean }[],
): Promise<UpdateCloudTestsResponse> =>
  patch(
    `${import.meta.env.VITE_API_BASE_URL}/${envId}/cloud-tests/tests`,
    tests,
  );

export const simulateCloudTestResult = (
  envId: string,
  resultId: string | undefined,
  payload: SimulateTestResultApi,
): Promise<TestResult> =>
  post(
    `${
      import.meta.env.VITE_API_BASE_URL
    }/${envId}/cloud-tests/test-results/${resultId}/simulate`,
    payload,
  );

export const getCloudTestResultEstimations = async (
  envId: string,
  resultId: string | undefined,
  sensitivities: AnomalySensitivity[],
): Promise<SensitivityEstimation[]> => {
  const result = await post(
    `${
      import.meta.env.VITE_API_BASE_URL
    }/${envId}/cloud-tests/test-results/${resultId}/estimate`,
    {
      sensitivities,
    },
  );
  return result.estimations;
};

export const pollTestsBulk = (
  envId: string,
  testIds: string[],
): Promise<{ results: TestPollingApi[] }> =>
  post(
    `${
      import.meta.env.VITE_API_BASE_URL
    }/${envId}/cloud-tests/tests/bulk-last-run-state`,
    { test_ids: testIds },
  );

export const fetchMetadata = (envId: string): Promise<FetchMetadataValuesApi> =>
  get(`${import.meta.env.VITE_API_BASE_URL}/${envId}/resources/metadata`);

export const setIsCriticalAssets = ({
  ids,
  isCritical,
  envId,
}: {
  ids: string[];
  isCritical: boolean;
  envId: string;
}) =>
  patch(`${import.meta.env.VITE_API_BASE_URL}/${envId}/assets/`, {
    asset_ids: ids,
    is_critical: isCritical,
  });

export const createCustomAlertOverride = (
  envId: string,
  destinations: CreateOverrideAlertRuleApi,
  entityIds: string[],
) =>
  post(
    `${
      import.meta.env.VITE_API_BASE_URL
    }/environments/${envId}/tests/override-alerts`,
    { destinations, entity_ids: entityIds },
  );

export const deleteRule = (ruleId: string, envId: string) =>
  del(
    `${
      import.meta.env.VITE_API_BASE_URL
    }/environments/${envId}/routing-rules/${ruleId}`,
    {},
  );

export const deleteDigestRule = (ruleId: string, envId: string) =>
  del(
    `${
      import.meta.env.VITE_API_BASE_URL
    }/environments/${envId}/digest-rules/${ruleId}`,
    {},
  );

export const deleteAlertDestinations = (
  envId: string,
  destinationIds: string[],
  entityIds: string[],
) =>
  del(
    `${
      import.meta.env.VITE_API_BASE_URL
    }/environments/${envId}/tests/override-alerts`,
    {
      data: {
        destination_ids: destinationIds,
        entity_ids: entityIds,
      },
    },
  );

export const fetchAlertIntegrations =
  (): Promise<FetchAlertIntegrationOptionsApi> =>
    get(`${import.meta.env.VITE_API_BASE_URL}/account/integrations/`);

export const addTests = (args: AddTestApi, envId: string, ticketId?: string) =>
  post(
    `${
      import.meta.env.VITE_API_BASE_URL
    }/environments/${envId}/pull-requests/add_generic_tests`,
    {
      data: { new_tests: args },
      ...(ticketId ? { ticket_id: ticketId } : {}),
    },
  );

export const addYamlTests = (
  args: AddTestApi,
  envId: string,
  ticketId?: string,
) =>
  post(
    `${
      import.meta.env.VITE_API_BASE_URL
    }/environments/${envId}/pull-requests/add_generic_tests`,
    {
      data: { yaml_tests: args },
      ...(ticketId ? { ticket_id: ticketId } : {}),
    },
  );

export const addSingularTest = (
  args: AddSingularTestApi,
  envId: string,
  ticketId?: string,
) =>
  post(
    `${
      import.meta.env.VITE_API_BASE_URL
    }/environments/${envId}/pull-requests/add_singular_test`,
    {
      data: args,
      ...(ticketId ? { ticket_id: ticketId } : {}),
    },
  );

export const checkDuplicateCloudTests = (
  args: AddCloudTestApi,
  envId: string,
) =>
  post(
    `${
      import.meta.env.VITE_API_BASE_URL
    }/${envId}/cloud-tests/tests/check-duplicates`,
    args,
  );

export const checkCreatableTests = (
  args: CheckCreatableTestsApi,
  envId: string,
) =>
  post(
    `${
      import.meta.env.VITE_API_BASE_URL
    }/test-management/${envId}/tests/check-creatable`,
    args,
  );

export const addCloudTest = (
  args: AddCloudTestApi,
  envId: string,
  override?: boolean,
) =>
  post(
    `${import.meta.env.VITE_API_BASE_URL}/${envId}/cloud-tests/tests${
      override ? '?override_existing=true' : ''
    }`,
    args,
  );

export const addCustomSqlCloudTest = (
  args: AddCustomSqlCloudTestApi,
  envId: string,
): Promise<{ test_id: string }> =>
  post(
    `${import.meta.env.VITE_API_BASE_URL}/${envId}/cloud-tests/sql-tests`,
    args,
  );

export const updateCustomSqlConfig = (
  envId: string,
  testId: string,
  config: UpdateCustomSqlConfigApi,
): Promise<{ test_id: string }> =>
  put(
    `${
      import.meta.env.VITE_API_BASE_URL
    }/${envId}/cloud-tests/tests/${testId}/sql-config`,
    config,
  );

export const convertQuery = (
  args: ConvertQueryApi,
  envId: string,
): Promise<ConvertQueryApiResult> =>
  post(
    `${
      import.meta.env.VITE_API_BASE_URL
    }/test-management/${envId}/convert-query`,
    args,
  );

export const generateCustomSql = (
  args: GenerateCustomSqlApi,
  envId: string,
): Promise<GenerateCustomSqlResult> =>
  post(
    `${
      import.meta.env.VITE_API_BASE_URL
    }/test-management/${envId}/ai-generate-custom-sql`,
    args,
  );

export const validateCustomSqlTest = (
  args: ValidateCustomSqlTestApi,
  envId: string,
): Promise<ValidateCustomSqlTestResult> =>
  post(
    `${
      import.meta.env.VITE_API_BASE_URL
    }/${envId}/cloud-tests/sql-tests/validate`,
    args,
  );

export const fetchDwh = (envId: string): Promise<FetchDwhApi> =>
  get(
    `${
      import.meta.env.VITE_API_BASE_URL
    }/test-management/data-warehouse/${envId}`,
  );

export const editGenericTest = (
  args: { data: { edit_tests: EditGenericTestApi[] } },
  envId: string,
  ticketId?: string,
): Promise<void> =>
  post(
    `${
      import.meta.env.VITE_API_BASE_URL
    }/environments/${envId}/pull-requests/edit_generic_tests`,
    {
      ...args,
      ...(ticketId ? { ticket_id: ticketId } : {}),
    },
  );

export const fetchDestinationOptions =
  (): Promise<FetchIntegrationDestinationOptionsApi> =>
    get(
      `${
        import.meta.env.VITE_API_BASE_URL
      }/account/integrations/destination-options`,
    );

export const muteTest = (envId: string, testId: string) =>
  post(
    `${
      import.meta.env.VITE_API_BASE_URL
    }/environments/${envId}/tests/${testId}/mute`,
  );

export const unMuteTest = (envId: string, testId: string) =>
  del(
    `${
      import.meta.env.VITE_API_BASE_URL
    }/environments/${envId}/tests/${testId}/mute`,
  );

export const editTestsNotification = (
  envId: string,
  ids: string[],
  mute: boolean,
) =>
  patch(
    `${import.meta.env.VITE_API_BASE_URL}/environments/${envId}/alert-configs`,
    {
      entity_ids: ids,
      is_muted: mute,
    },
  );

// Callers MUST filter on `canCreateTests`: this resolver only authorizes
// `AssetPermission.VIEW`, while test creation needs `CREATE_TESTS`.
export const getTestManagementAssetsQuery = gql`
  query GetTestManagementAssets {
    tableAssets(
      filters: { excludeTableTypes: [SEED, SNAPSHOT] }
      includeInternalAssets: false
      limit: 9000
    ) {
      items {
        id
        name
        dbName
        schemaName
        tableName
        materialization
        tableType
        sourceType
        sourceName
        fqn
        tags
        owners
        canCreateTests
        cloudTestsSupported {
          freshnessAnomalies
          volumeAnomalies
          dataContract
          pipelineTaskPerformance
        }
      }
    }
  }
`;

export const getTestManagementAssetColumnsQuery = gql`
  query GetTestManagementAssetColumns {
    tableAssetColumns(
      filters: { excludeTableTypes: [SEED, SNAPSHOT] }
      includeInternalAssets: false
      onlyTestCreatable: true
    )
  }
`;

export const getTestManagementTestsQuery = gql`
  query GetTestManagementTests($includeAlertDestinations: Boolean = false) {
    tests(testSources: [DBT, CLOUD]) {
      id
      name
      type
      sourceType
      description
      severity
      owners
      tags
      args
      testMetadata
      isSingular
      isCustomSql
      columnName
      testCategory
      namespace
      path
      updatedAt
      config {
        muted
        type
      }
      alertDestinations @include(if: $includeAlertDestinations) {
        id
        type
        channel
        configurationType
        ruleId
      }
      tableAsset {
        id
        name
        dbName
        schemaName
        tableName
        sourceName
        owners
        tags
        materialization
        lastDataModificationTime
      }
    }
  }
`;

export const getAssetQuery = gql`
  query GetAsset($id: String!, $includeTestExecutions: Boolean = false) {
    asset(id: $id) {
      id
      name
      type
      canCreateTests
      displayType
      artifactType
      description
      owners
      subscribers
      tags
      fqn
      parentId
      path
      additionalProperties
      columns {
        id
        name
        displayName
        dataType
        description
        tags
      }
      testExecutionsByQualityDimension @include(if: $includeTestExecutions) {
        dimension
        totals {
          passed
          warnings
          failures
          errors
          skipped
        }
      }
      ... on TableAsset {
        configPath
        dbName
        schemaName
        tableName
        materialization
        lastDataModificationTime
        latestExecution {
          id
          status
          executionTimestamp
          query
        }
        latestTestExecutions {
          test {
            name
          }
          execution {
            status
            subType
            executionTimestamp
            columnName
          }
        }
        tableUsage {
          periodStart
          periodEnd
          queriesCount
          uniqueUserCount
          usagePercentile
        }
      }
      ... on BiAsset {
        platform
        biType
        url
        rawQueries
      }
      ... on SemanticAsset {
        platform
        semanticType
        dbName
        schemaName
      }
      ... on EtlAsset {
        latestExecution {
          id
          status
          executionTimestamp
          query
        }
      }
    }
  }
`;

export const getSemanticElementsQuery = gql`
  query GetSemanticElements($parentIds: [String!]!) {
    assets(parentIds: $parentIds) {
      items {
        id
        name
        description
        artifactType
        columns {
          id
          name
          displayName
          dataType
          description
        }
        ... on SemanticAsset {
          semanticType
        }
      }
    }
  }
`;

export const getLatestExecutionQuery = gql`
  query GetLatestExecution($id: String!) {
    asset(id: $id) {
      ... on TableAsset {
        latestExecution(ignoreSkippedRuns: true) {
          query
        }
      }
    }
  }
`;

export const getTestDataQuery = gql`
  query GetTestData($id: String!) {
    latestTestExecution(id: $id) {
      test {
        id
        name
        description
        path
      }
      execution {
        id
        status
        executionTimestamp
        query
      }
    }
  }
`;
